import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { postOrQueue } from "@/lib/offlineSync";
import OfflineSyncBanner from "@/components/OfflineSyncBanner";
import AccessLimitReachedCard from "@/components/AccessLimitReachedCard";
import ExitConfirmDialog from "@/components/ExitConfirmDialog";
import ShareButtons from "@/components/ShareButtons";
import CelebrateShareBanner from "@/components/CelebrateShareBanner";
import { useExitGuard } from "@/hooks/useExitGuard";
import { webAppUrl } from "@/lib/platform";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import {
  ChevronLeft, CheckCircle2, XCircle, Sparkles, ArrowRight, Loader2,
  ChevronDown, ChevronUp, RotateCcw, BookOpen, RefreshCw, Trophy,
} from "lucide-react";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildSession(rawQuestions) {
  const filtered = rawQuestions.filter((q) => q.choice_a && q.choice_b);
  const shuffled = shuffle(filtered);
  return shuffled.map((q) => {
    const originals = [
      { original: "a", text: q.choice_a },
      { original: "b", text: q.choice_b },
      { original: "c", text: q.choice_c },
      { original: "d", text: q.choice_d },
    ].filter((c) => c.text && c.text.trim() !== "");
    const displayChoices = shuffle(originals).map((c, i) => ({
      ...c, display: String.fromCharCode(65 + i),
    }));
    return { ...q, displayChoices };
  });
}

export default function StudentReview() {
  const { moduleId } = useParams();
  const nav = useNavigate();
  const [allQuestions, setAllQuestions] = useState([]);
  const [questions, setQuestions] = useState([]); // current session questions (with displayChoices)
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [score, setScore] = useState({ c: 0, t: 0 });
  const [done, setDone] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [showAllChoices, setShowAllChoices] = useState(false);
  const [wrongList, setWrongList] = useState([]); // [{question(with displayChoices), selected, correctDisplay, explanation}]
  const [mode, setMode] = useState("exam"); // exam | review-wrong
  const [reviewIdx, setReviewIdx] = useState(0);
  const [isRetry, setIsRetry] = useState(false);
  const [breadcrumb, setBreadcrumb] = useState(null);
  const [startedAt] = useState(() => new Date());
  const [accessState, setAccessState] = useState(null);

  // Track when the current question was first shown — fed into FSRS as the
  // confidence signal (fast answer → Easy, slow answer → Hard).
  const shownAtRef = useRef(Date.now());
  useEffect(() => {
    shownAtRef.current = Date.now();
  }, [idx, questions.length]);

  // Exit confirmation guard — active during an in-progress exam session
  const inProgress = mode === "exam" && !done && questions.length > 0;
  const { confirmOpen, confirmExit, cancelExit } = useExitGuard(inProgress);

  useEffect(() => {
    api.get(`/questions?module_id=${moduleId}`).then((r) => {
      const fetched = r.data || [];
      setAllQuestions(fetched);
      setQuestions(buildSession(fetched));
    });
    api.get(`/modules/${moduleId}/breadcrumb`).then((r) => setBreadcrumb(r.data)).catch(() => {});
    api.get("/student/access-status").then((r) => setAccessState(r.data)).catch(() => {});
  }, [moduleId]);

  const current = questions[idx];

  const submit = async () => {
    if (!selected || !current) return;
    setSubmitting(true);
    try {
      const r = await postOrQueue(api, "/student/answer", {
        question_id: current.id,
        selected_answer: selected.original.toUpperCase(),
        time_taken_ms: Math.max(0, Date.now() - shownAtRef.current),
      });
      if (r.queued) {
        toast.info("Saved offline — will sync when you reconnect", { duration: 4000 });
        // Optimistically advance without feedback (we don't know correctness offline)
        setResult(null);
        setScore((s) => ({ c: s.c, t: s.t + 1 }));
        return;
      }
      if (!r.ok) { toast.error(r.error?.response?.data?.detail || "Failed"); return; }
      const correctDisplay = current.displayChoices.find(
        (c) => c.original === r.data.correct_answer.toLowerCase()
      );
      setResult({ ...r.data, correctDisplay });
      setScore((s) => ({ c: s.c + (r.data.is_correct ? 1 : 0), t: s.t + 1 }));
      if (!r.data.is_correct) {
        setWrongList((w) => [
          ...w,
          {
            question: current,
            selected,
            correctDisplay,
            explanation: r.data.explanation || null,
            correct_original: r.data.correct_answer,
          },
        ]);
      }
      if (r.data.new_badges && r.data.new_badges.length > 0) {
        r.data.new_badges.forEach((b) =>
          toast.success(`🏆 Badge unlocked: ${b.name}`, { description: b.description, duration: 5000 })
        );
      }
      // Live-update the quota pill (Exam Mode) from the answer response.
      if (r.data.access_state) {
        const prev = accessState;
        setAccessState(r.data.access_state);
        // Fire a one-time "last free question" toast at the highest pay-intent moment.
        if (
          r.data.access_state.is_limited &&
          r.data.access_state.questions_remaining === 1 &&
          prev?.questions_remaining !== 1
        ) {
          toast.warning("Last free question — upgrade to keep going.", {
            duration: 6000,
            description: "Pay any time from your dashboard to unlock full access.",
          });
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => {
    if (idx + 1 >= questions.length) { setDone(true); return; }
    setIdx((i) => i + 1);
    setSelected(null);
    setResult(null);
    setAiOpen(false);
    setShowAllChoices(false);
  };

  const renderText = (text, keyword) => {
    if (!keyword || !text.toLowerCase().includes(keyword.toLowerCase())) return text;
    const i = text.toLowerCase().indexOf(keyword.toLowerCase());
    return (<>{text.slice(0, i)}<span className="keyword-highlight">{text.slice(i, i + keyword.length)}</span>{text.slice(i + keyword.length)}</>);
  };

  /* ---- session controls ---- */
  const startNewExam = () => {
    setQuestions(buildSession(allQuestions));
    setIdx(0); setSelected(null); setResult(null); setScore({ c: 0, t: 0 });
    setDone(false); setWrongList([]); setMode("exam"); setReviewIdx(0); setIsRetry(false);
    setAiOpen(false); setShowAllChoices(false);
  };

  const repeatWrong = () => {
    if (wrongList.length === 0) return;
    // Rebuild a fresh session using ONLY the wrong question source data, reshuffled
    const wrongRaw = wrongList.map((w) => allQuestions.find((q) => q.id === w.question.id)).filter(Boolean);
    setQuestions(buildSession(wrongRaw));
    setIdx(0); setSelected(null); setResult(null); setScore({ c: 0, t: 0 });
    setDone(false); setWrongList([]); setMode("exam"); setIsRetry(true);
    setAiOpen(false); setShowAllChoices(false);
  };

  const enterReviewWrong = () => { setMode("review-wrong"); setReviewIdx(0); setAiOpen(false); setShowAllChoices(false); };
  const exitReviewWrong = () => { setMode("exam"); };

  /* ---------------- RENDER ---------------- */

  if (questions.length === 0 && allQuestions.length === 0) {
    const limitReached = accessState && !accessState.is_full && accessState.questions_remaining === 0;
    if (limitReached) {
      return (
        <div className="space-y-6 max-w-2xl mx-auto">
          <Link to="/student/courses" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" />Back</Link>
          <AccessLimitReachedCard accessState={accessState} />
        </div>
      );
    }
    return (
      <div className="space-y-6">
        <Link to="/student/courses" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" />Back</Link>
        <Card className="p-10 text-center text-slate-500">No questions in this module yet.</Card>
      </div>
    );
  }

  /* Review-incorrect (read-only) mode */
  if (mode === "review-wrong") {
    const total = wrongList.length;
    if (total === 0 || reviewIdx >= total) return null;
    const w = wrongList[reviewIdx];
    return (
      <div className="max-w-3xl mx-auto space-y-6" data-testid="review-wrong-mode">
        <button onClick={exitReviewWrong} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Back to Results
        </button>
        <div className="flex items-center justify-between">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-rose-600">Incorrect {reviewIdx + 1} of {total}</div>
          <div className="text-xs text-slate-500">Review only — score won't change</div>
        </div>
        <Progress value={((reviewIdx + 1) / total) * 100} className="h-2" />

        <Card className="p-6 sm:p-8 border-slate-200">
          <p className="text-xl leading-relaxed text-slate-900 font-medium">{renderText(w.question.question_text, w.question.keyword)}</p>

          <div className="mt-6 space-y-3">
            <div className="p-4 rounded-md border border-rose-300 bg-rose-50">
              <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700">Your Answer</div>
              <div className="text-sm font-semibold text-slate-900 mt-1">
                {w.selected.display}. {w.selected.text}
              </div>
            </div>
            <div className="p-4 rounded-md border border-emerald-300 bg-emerald-50">
              <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Correct Answer</div>
              <div className="text-sm font-semibold text-slate-900 mt-1">
                {w.correctDisplay?.display}. {w.correctDisplay?.text}
              </div>
            </div>
          </div>

          <Button
            variant="outline"
            className="mt-6 w-full h-11 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            onClick={() => setAiOpen(true)}
            data-testid="explain-with-ai-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" strokeWidth={2} /> Explain with AI
          </Button>

          <Button
            className="mt-3 w-full h-11 bg-[#1E3A8A] hover:bg-[#1E40AF]"
            onClick={() => {
              if (reviewIdx + 1 >= total) { exitReviewWrong(); return; }
              setReviewIdx(reviewIdx + 1); setAiOpen(false); setShowAllChoices(false);
            }}
            data-testid="next-incorrect-btn"
          >
            {reviewIdx + 1 >= total ? "Finish Review" : "Next Incorrect Question"} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Card>

        <AIExplanationDialog
          open={aiOpen}
          onOpenChange={setAiOpen}
          question={w.question}
          selected={w.selected}
          correctDisplay={w.correctDisplay}
          correctOriginal={w.correct_original}
          explanation={w.explanation}
          showAllChoices={showAllChoices}
          setShowAllChoices={setShowAllChoices}
        />
      </div>
    );
  }

  /* Exam-completed result screen */
  if (done) {
    const pct = score.t > 0 ? Math.round((score.c / score.t) * 100) : 0;
    const allCorrect = wrongList.length === 0;
    const elapsedMin = Math.max(1, Math.round((Date.now() - startedAt.getTime()) / 60000));
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="review-complete">
        <Card className="p-6 sm:p-10 border-slate-200 overflow-hidden relative">
          <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-blue-100/50 blur-2xl pointer-events-none" />
          <div className="relative text-center">
            <div className={`mx-auto h-14 w-14 rounded-full grid place-items-center mb-3 ${allCorrect ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
              {allCorrect ? <Trophy className="w-7 h-7" /> : <CheckCircle2 className="w-7 h-7" />}
            </div>
            <h2 className="font-display text-2xl font-bold">
              {isRetry ? "Retry Complete!" : "Exam Complete!"}
            </h2>
            {breadcrumb && (
              <p className="text-xs text-slate-500 mt-1 truncate">
                {breadcrumb.course_name} · {breadcrumb.subject_name} · {breadcrumb.module_title}
              </p>
            )}

            <div className="mt-6 grid grid-cols-3 gap-2 max-w-md mx-auto" data-testid="result-stats">
              <Stat label="Score" value={`${pct}%`} highlight />
              <Stat label="Correct" value={`${score.c}`} tone="emerald" />
              <Stat label="Wrong" value={`${wrongList.length}`} tone={wrongList.length ? "rose" : "slate"} />
            </div>
            <p className="text-xs text-slate-500 mt-3">
              {score.c} of {score.t} correct · {elapsedMin} min
            </p>

            {allCorrect ? (
              <div className="mt-6 p-4 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-medium" data-testid="all-correct-banner">
                Excellent work! You answered all questions correctly.
              </div>
            ) : (
              <p className="mt-6 text-sm text-slate-700">
                You answered <span className="font-bold">{wrongList.length}</span> question{wrongList.length === 1 ? "" : "s"} incorrectly.
                Review or repeat them now to strengthen your weak areas.
              </p>
            )}

            <div className="mt-6 grid sm:grid-cols-2 gap-2.5">
              {!allCorrect && (
                <>
                  <Button onClick={repeatWrong} className="bg-[#1E3A8A] hover:bg-[#1E40AF] h-11" data-testid="repeat-wrong-btn">
                    <RefreshCw className="w-4 h-4 mr-2" /> {isRetry ? "Repeat Still Incorrect" : "Repeat Wrong Answers"}
                  </Button>
                  <Button onClick={enterReviewWrong} variant="outline" className="h-11 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="review-wrong-btn">
                    <BookOpen className="w-4 h-4 mr-2" /> {isRetry ? "Review Still Incorrect" : "Review Incorrect Answers"}
                  </Button>
                </>
              )}
              <Button onClick={() => nav(-1)} variant="outline" className="h-11" data-testid="back-to-module-btn">
                <ChevronLeft className="w-4 h-4 mr-2" /> Back to Module
              </Button>
              {!isRetry && (
                <Button onClick={startNewExam} variant="outline" className="h-11" data-testid="new-exam-btn">
                  <RotateCcw className="w-4 h-4 mr-2" /> Start New Exam
                </Button>
              )}
            </div>

            {!isRetry && score.t > 0 && breadcrumb && (
              <div className="mt-6 pt-5 border-t border-slate-100" data-testid="exam-share-section">
                {pct === 100 && (
                  <CelebrateShareBanner
                    title="💯 Perfect score — share the brag!"
                    subtitle="100% is a flex worth showing off. Your share helps friends find PrepMaster."
                    testId="celebrate-exam-share"
                  />
                )}
                <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                  Share your result
                </div>
                <div className="flex justify-center">
                  <ShareButtons
                    getShareUrl={async () => {
                      const r = await api.post("/student/share/exam-result", {
                        course_name: breadcrumb.course_name,
                        module_name: breadcrumb.module_title,
                        module_id: breadcrumb.module_id,
                        correct: score.c,
                        total: score.t,
                        score_percentage: pct,
                      });
                      return {
                        url: `${webAppUrl()}/share/exam/${r.data.share_token}`,
                        newly_awarded_badge: r.data.newly_awarded_badge,
                      };
                    }}
                    defaultText={`I just scored ${pct}% on ${breadcrumb.module_title} (${breadcrumb.course_name}) on PrepMaster! 📚`}
                    testIdPrefix="exam-share"
                  />
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    );
  }

  if (!current) return null;

  /* Exam-in-progress UI (unchanged from prior version) */
  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="student-review">
      <Link to="/student/courses" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" />Exit {isRetry ? "Retry" : "Review"}</Link>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">
          {isRetry && <span className="text-violet-700 mr-2">RETRY</span>}
          Question {idx + 1} of {questions.length}
        </div>
        <div className="flex items-center gap-3">
          {accessState && accessState.is_limited && accessState.question_limit > 0 && (
            <div
              className="px-3 py-1.5 rounded-full bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-amber-900 dark:text-amber-100 text-xs font-semibold"
              data-testid="exam-mode-quota-pill"
            >
              Temporary access · {accessState.questions_remaining} of {accessState.question_limit} left
            </div>
          )}
          <div className="text-sm text-slate-500">Score: {score.c}/{score.t}</div>
        </div>
      </div>
      <Progress value={(idx / questions.length) * 100} className="h-2" />

      <OfflineSyncBanner />

      <Card className="p-6 sm:p-8 border-slate-200">
        <p className="text-xl leading-relaxed text-slate-900 font-medium">{renderText(current.question_text, current.keyword)}</p>
        <div className="mt-6 space-y-3">
          {current.displayChoices.map((c) => {
            const isSelected = selected?.original === c.original;
            const isCorrect = result && result.correct_answer && result.correct_answer.toLowerCase() === c.original;
            const isWrongSelection = result && isSelected && !result.is_correct;
            return (
              <button
                key={c.original}
                disabled={!!result}
                onClick={() => setSelected({ display: c.display, original: c.original, text: c.text })}
                data-testid={`choice-${c.display}`}
                className={`w-full text-left p-4 rounded-md border transition-all ${
                  isCorrect ? "border-green-400 bg-green-50" :
                  isWrongSelection ? "border-red-400 bg-red-50" :
                  isSelected ? "border-blue-500 bg-blue-50" :
                  "border-slate-200 hover:border-slate-300 bg-white"
                }`}
              >
                <span className="font-bold mr-2">{c.display}.</span>
                <span>{c.text}</span>
              </button>
            );
          })}
        </div>

        {!result ? (
          <Button className="mt-6 w-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-11" disabled={!selected || submitting} onClick={submit} data-testid="submit-answer-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Answer"}
          </Button>
        ) : (
          <>
            <div className={`mt-6 p-4 rounded-md border ${result.is_correct ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`}>
              <div className="flex items-center gap-2 font-bold">
                {result.is_correct
                  ? <><CheckCircle2 className="w-5 h-5" /> Correct!</>
                  : <><XCircle className="w-5 h-5" /> Incorrect</>}
              </div>
              {!result.is_correct && result.correctDisplay && (
                <div className="mt-2 text-sm">
                  <span className="text-slate-600">Correct answer:</span>{" "}
                  <span className="font-semibold text-slate-900">{result.correctDisplay.display}. {result.correctDisplay.text}</span>
                </div>
              )}
            </div>

            <Button className="mt-6 w-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-11" onClick={next} data-testid="next-question-btn">
              {idx + 1 >= questions.length ? "Finish" : "Next Question"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            {/* Unified Rationale: one button that opens ONE dialog. The
                dialog shows the AI-generated per-choice explanation if
                available; if not, it falls back to the imported plain
                rationale text on the question (see AIExplanationDialog
                below). Prev UX had a separate "Rationale" popup + this
                button which confused students. */}
            <Button
              variant="outline"
              className="mt-3 w-full h-11 border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800"
              onClick={() => setAiOpen(true)}
              data-testid="rationale-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" strokeWidth={2} /> Rationale
            </Button>
          </>
        )}
      </Card>

      <AIExplanationDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        question={current}
        selected={selected}
        correctDisplay={result?.correctDisplay}
        correctOriginal={result?.correct_answer}
        explanation={result?.explanation}
        isCorrect={result?.is_correct}
        showAllChoices={showAllChoices}
        setShowAllChoices={setShowAllChoices}
      />

      <ExitConfirmDialog
        open={confirmOpen}
        onCancel={cancelExit}
        onConfirm={confirmExit}
        mode="exam"
      />
    </div>
  );
}

function Stat({ label, value, highlight, tone = "blue" }) {
  const map = {
    blue: "text-blue-700", emerald: "text-emerald-700", rose: "text-rose-700", slate: "text-slate-700",
  };
  return (
    <div className={`rounded-lg p-3 ${highlight ? "bg-blue-50 border border-blue-200" : "bg-slate-50 border border-slate-100"}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div className={`font-display font-extrabold text-2xl leading-none mt-1 ${highlight ? "text-blue-700" : map[tone]}`}>{value}</div>
    </div>
  );
}

function AIExplanationDialog({ open, onOpenChange, question, selected, correctDisplay, correctOriginal, explanation, isCorrect, showAllChoices, setShowAllChoices }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-600" /> Rationale
          </DialogTitle>
          <DialogDescription>Review the explanation for this question.</DialogDescription>
        </DialogHeader>

        {!explanation ? (
          // Fall back to the imported plain-text rationale on the question
          // when no AI-generated rationale exists yet. Difficulty-word
          // junk is filtered out.
          ((() => {
            const rat = (question?.rationale || "").trim();
            const isDifficultyWord = /^(easy|average|difficult|medium|hard)$/i.test(rat);
            return rat && !isDifficultyWord ? (
              <div className="space-y-2 py-2" data-testid="exam-rationale-plain-fallback">
                <div className="text-xs font-bold text-indigo-700 uppercase tracking-wide">Explanation</div>
                <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-line">{rat}</p>
              </div>
            ) : (
              <div className="py-8 text-center">
                <div className="mx-auto h-12 w-12 rounded-full bg-slate-100 grid place-items-center text-slate-400 mb-3">
                  <Sparkles className="w-6 h-6" />
                </div>
                <p className="text-sm text-slate-600">No rationale is available for this question yet.</p>
              </div>
            );
          })())
        ) : correctDisplay ? (
          <div className="space-y-4">
            <div className="ai-card p-4">
              <div className="text-xs font-bold text-indigo-700 uppercase tracking-wide">Correct Answer</div>
              <div className="text-sm font-semibold text-slate-900 mt-1 mb-3">
                {correctDisplay.display}. {correctDisplay.text}
              </div>
              <div className="text-xs font-bold text-indigo-700 uppercase tracking-wide mb-1">Why it is correct</div>
              <p className="text-sm leading-relaxed text-slate-700">{explanation.correct_answer_explanation}</p>
            </div>

            {selected && !isCorrect && explanation[`choice_${selected.original}_explanation`] && (
              <div className="p-4 rounded-md border border-slate-200 bg-slate-50">
                <div className="text-xs font-bold text-slate-600 uppercase tracking-wide">Your Answer</div>
                <div className="text-sm font-semibold text-slate-900 mt-1 mb-3">
                  {selected.display}. {selected.text}
                </div>
                <div className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Why it is incorrect</div>
                <p className="text-sm leading-relaxed text-slate-700">{explanation[`choice_${selected.original}_explanation`]}</p>
              </div>
            )}

            <Collapsible open={showAllChoices} onOpenChange={setShowAllChoices}>
              <CollapsibleTrigger asChild>
                <button className="w-full flex items-center justify-between p-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700" data-testid="toggle-all-choices">
                  <span>View explanation for all choices</span>
                  {showAllChoices ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-3 space-y-3">
                {question?.displayChoices?.map((c) => {
                  const exp = explanation[`choice_${c.original}_explanation`];
                  if (!exp) return null;
                  const isC = (correctOriginal || "").toLowerCase() === c.original;
                  return (
                    <div key={c.original} className={`p-3 rounded-md border ${isC ? "bg-green-50 border-green-200" : "bg-slate-50 border-slate-200"}`}>
                      <div className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">
                        {c.display}. {c.text} {isC && <span className="text-green-700 ml-1">(Correct)</span>}
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">{exp}</p>
                    </div>
                  );
                })}
              </CollapsibleContent>
            </Collapsible>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-ai-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
